import { useQuery } from "@tanstack/react-query";
import {
  Card,
  CardHeader,
  ErrorState,
  PageHeader,
  Skeleton,
} from "@/components/ui";
import { useApi } from "@/lib/api";
import { cn } from "@/lib/cn";
import { count, percent } from "@/lib/format";
import { buildGroups, type Goal, type Unit } from "./goals.config";

/**
 * Goals and milestones, scored against live numbers.
 *
 * It adds no backend surface: every `current` is derived from the same
 * `/admin/dashboard` payload the dashboard already loads, which is what
 * makes a goal impossible to disagree with the dashboard about. The targets
 * themselves live in `goals.config.ts` — one file, clearly marked, edited in
 * a commit.
 */
function format(value: number, unit: Unit): string {
  return unit === "percent" ? percent(value) : count(Math.round(value));
}

function GoalRow({ goal }: { goal: Goal }) {
  const done = goal.current >= goal.target;
  const pct = Math.min(100, (goal.current / goal.target) * 100);

  return (
    <li className="flex flex-col gap-1.5 border-b border-border-2 py-2.5 last:border-b-0">
      <div className="flex items-start justify-between gap-3">
        <span className="flex items-center gap-1.5 text-[12.5px] text-ink-2">
          <span
            aria-hidden="true"
            className={cn(
              "inline-flex h-3.5 w-3.5 shrink-0 items-center justify-center rounded-full border text-[8px] leading-none",
              done
                ? "border-success bg-success text-accent-ink"
                : "border-border-2 text-transparent",
            )}
          >
            ✓
          </span>
          <span className={cn(done && "text-ink")}>{goal.label}</span>
        </span>
        <span className="shrink-0 font-mono text-[11.5px] text-ink-2">
          {format(goal.current, goal.unit)}
          <span className="text-muted-2">
            {" / "}
            {format(goal.target, goal.unit)}
          </span>
        </span>
      </div>
      <div className="h-1 w-full overflow-hidden rounded-full bg-surface-3">
        <div
          className={cn(
            "h-full rounded-full transition-[width] duration-500",
            done ? "bg-success" : "bg-accent",
          )}
          style={{ width: `${pct}%` }}
        />
      </div>
      {goal.note ? (
        <span className="text-micro text-muted">{goal.note}</span>
      ) : null}
    </li>
  );
}

export function GoalsScreen() {
  const api = useApi();
  const dashboard = useQuery({
    queryKey: ["admin", "dashboard"],
    queryFn: async () => {
      const { data, error } = await api.GET("/admin/dashboard");
      if (error) throw error;
      return data;
    },
  });

  if (dashboard.isError) {
    return (
      <div className="flex flex-col gap-4">
        <PageHeader eyebrow="Overview" title="Goals" />
        <ErrorState
          message="Could not load the numbers behind these goals."
          onRetry={() => void dashboard.refetch()}
        />
      </div>
    );
  }

  const groups = dashboard.data ? buildGroups(dashboard.data) : [];
  const all = groups.flatMap((g) => g.goals);
  const reached = all.filter((g) => g.current >= g.target).length;

  return (
    <div className="flex flex-col gap-5">
      <PageHeader
        eyebrow="Overview"
        title="Goals and milestones"
        description={
          dashboard.data
            ? `${reached} of ${all.length} reached · real accounts only, internal logins excluded. Targets live in src/screens/goals.config.ts.`
            : "Real accounts only — internal logins are excluded."
        }
      />

      {!dashboard.data ? (
        <div className="grid gap-3 lg:grid-cols-3">
          {Array.from({ length: 3 }, (_, i) => (
            <Skeleton key={i} className="h-72" />
          ))}
        </div>
      ) : (
        <section className="grid items-start gap-3 lg:grid-cols-3">
          {groups.map((group) => {
            const groupReached = group.goals.filter(
              (g) => g.current >= g.target,
            ).length;
            return (
              <Card key={group.category}>
                <CardHeader
                  title={group.category}
                  subtitle={group.blurb}
                  action={
                    <span className="font-mono text-[11.5px] text-muted">
                      {groupReached}/{group.goals.length}
                    </span>
                  }
                />
                <ul className="flex flex-col">
                  {group.goals.map((goal) => (
                    <GoalRow key={goal.label} goal={goal} />
                  ))}
                </ul>
              </Card>
            );
          })}
        </section>
      )}
    </div>
  );
}
